import React, { useEffect, useRef, useState } from 'react';
import { api, extractApiError } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Palette, Ruler, Save, RotateCcw, Upload, Image as ImageIcon, Loader2, LayoutList, Plus, Trash2, ArrowUp, ArrowDown, CreditCard, ShieldCheck, KeyRound } from 'lucide-react';

/**
 * AdminSettingsTab — Phase 7D.1
 * Combines Brand Settings (C12) and Content Rules (C15) under a single tab
 * with two sub-tabs. Each save action is scoped to its section — brand
 * changes never trigger a content_rules write and vice versa.
 */
const AdminSettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [brand, setBrand] = useState(null);
  const [rules, setRules] = useState(null);
  const [footer, setFooter] = useState(null);
  const [featureToggles, setFeatureToggles] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/settings');
      setBrand(data.brand || {});
      setRules(data.content_rules || {});
      setFooter(data.footer || { columns: [], about_text: '', copyright: '', bottom_tagline: '', show_social: true });
      setFeatureToggles(data.feature_toggles || { show_upcoming_earn_categories: false });
    } catch (e) {
      toast.error(extractApiError(e, 'Failed to load settings'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const saveFeatureToggles = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/admin/settings/feature_toggles', { updates: featureToggles });
      setFeatureToggles(data.data);
      toast.success('Feature toggles saved');
    } catch (e) {
      toast.error(extractApiError(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  const saveBrand = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/admin/settings/brand', { updates: brand });
      setBrand(data.data);
      toast.success('Brand settings saved');
    } catch (e) {
      toast.error(extractApiError(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  const saveRules = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/admin/settings/content_rules', { updates: rules });
      setRules(data.data);
      toast.success('Content rules saved');
    } catch (e) {
      toast.error(extractApiError(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  const saveFooter = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/admin/settings/footer', { updates: footer });
      setFooter(data.data);
      toast.success('Footer saved');
    } catch (e) {
      toast.error(extractApiError(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  const resetBrand = async () => {
    if (!window.confirm('Reset Brand settings to factory defaults? Your logo & customisations will be lost.')) return;
    setSaving(true);
    try {
      const { data } = await api.post('/admin/settings/brand/reset');
      setBrand(data.data);
      toast.success('Brand settings reset to defaults');
    } catch (e) {
      toast.error(extractApiError(e, 'Reset failed'));
    } finally { setSaving(false); }
  };

  const resetRules = async () => {
    if (!window.confirm('Reset content rules to factory defaults?')) return;
    setSaving(true);
    try {
      const { data } = await api.post('/admin/settings/content_rules/reset');
      setRules(data.data);
      toast.success('Content rules reset');
    } catch (e) { toast.error(extractApiError(e, 'Reset failed')); }
    finally { setSaving(false); }
  };

  const resetFooter = async () => {
    if (!window.confirm('Reset the footer to factory defaults? Any custom columns or links will be lost.')) return;
    setSaving(true);
    try {
      const { data } = await api.post('/admin/settings/footer/reset');
      setFooter(data.data);
      toast.success('Footer reset');
    } catch (e) { toast.error(extractApiError(e, 'Reset failed')); }
    finally { setSaving(false); }
  };

  if (loading || !brand || !rules || !footer) {
    return <Card className='p-6 text-sm text-muted-foreground' data-testid='admin-settings-loading'>Loading platform settings…</Card>;
  }

  return (
    <div className='space-y-4' data-testid='admin-settings-tab'>
      <Tabs defaultValue='brand'>
        <TabsList data-testid='admin-settings-subtabs'>
          <TabsTrigger value='brand' data-testid='settings-subtab-brand'>
            <Palette className='h-4 w-4 mr-1' /> Brand
          </TabsTrigger>
          <TabsTrigger value='rules' data-testid='settings-subtab-rules'>
            <Ruler className='h-4 w-4 mr-1' /> Content rules
          </TabsTrigger>
          <TabsTrigger value='footer' data-testid='settings-subtab-footer'>
            <LayoutList className='h-4 w-4 mr-1' /> Footer
          </TabsTrigger>
          <TabsTrigger value='payments' data-testid='settings-subtab-payments'>
            <CreditCard className='h-4 w-4 mr-1' /> Payments
          </TabsTrigger>
          <TabsTrigger value='features' data-testid='settings-subtab-features'>
            <Ruler className='h-4 w-4 mr-1' /> Feature toggles
          </TabsTrigger>
        </TabsList>

        <TabsContent value='brand' className='space-y-4'>
          <BrandForm brand={brand} setBrand={setBrand} saving={saving} onSave={saveBrand} onReset={resetBrand} />
        </TabsContent>

        <TabsContent value='rules' className='space-y-4'>
          <RulesForm rules={rules} setRules={setRules} saving={saving} onSave={saveRules} onReset={resetRules} />
        </TabsContent>

        <TabsContent value='footer' className='space-y-4'>
          <FooterForm footer={footer} setFooter={setFooter} saving={saving} onSave={saveFooter} onReset={resetFooter} />
        </TabsContent>

        <TabsContent value='payments' className='space-y-4'>
          <RazorpayConfigCard />
        </TabsContent>

        <TabsContent value='features' className='space-y-4'>
          <Card className='p-5 space-y-4' data-testid='features-settings-form'>
            <div>
              <div className='text-lg font-semibold'>Feature toggles</div>
              <p className='text-sm text-muted-foreground mt-0.5'>
                Turn in-progress or pre-launch features on/off without a redeploy. Changes take effect on next page load for logged-in users.
              </p>
            </div>
            <div className='space-y-3'>
              <label className='flex items-start gap-3 p-3 rounded-md border cursor-pointer hover:bg-secondary/40'>
                <input
                  type='checkbox'
                  className='mt-1 h-4 w-4'
                  checked={!!featureToggles?.show_upcoming_earn_categories}
                  onChange={(e) => setFeatureToggles({ ...featureToggles, show_upcoming_earn_categories: e.target.checked })}
                  data-testid='ft-show-upcoming-earn'
                />
                <div>
                  <div className='text-sm font-medium'>Show "Community (Coming Soon)" earn categories</div>
                  <div className='text-xs text-muted-foreground mt-0.5'>
                    Reveals Forum, Kaizen and other roadmap categories on the Invite &amp; Earn → Ways to Earn page. Keep OFF until those modules launch.
                  </div>
                </div>
              </label>
            </div>
            <div className='flex justify-end'>
              <Button onClick={saveFeatureToggles} disabled={saving} data-testid='save-feature-toggles'>
                {saving ? 'Saving…' : 'Save toggles'}
              </Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Brand form
// ---------------------------------------------------------------------------
const BrandForm = ({ brand, setBrand, saving, onSave, onReset }) => {
  const set = (k, v) => setBrand({ ...brand, [k]: v });
  const setSocial = (k, v) => setBrand({ ...brand, social: { ...(brand.social || {}), [k]: v } });

  return (
    <Card className='p-5 space-y-6' data-testid='brand-settings-form'>
      <div>
        <div className='text-lg font-semibold'>Brand identity</div>
        <p className='text-sm text-muted-foreground mt-0.5'>These appear across the header, footer, meta tags, and email templates.</p>
      </div>

      <div className='grid md:grid-cols-2 gap-4'>
        <FormRow label='Brand name'>
          <Input value={brand.brand_name || ''} onChange={e => set('brand_name', e.target.value)} data-testid='brand-name' />
        </FormRow>
        <FormRow label='Short name (for header)'>
          <Input value={brand.brand_short_name || ''} onChange={e => set('brand_short_name', e.target.value)} data-testid='brand-short-name' />
        </FormRow>
      </div>

      <FormRow label='Tagline'>
        <Input value={brand.tagline || ''} onChange={e => set('tagline', e.target.value)} maxLength={200} data-testid='brand-tagline' />
      </FormRow>

      <div className='grid md:grid-cols-3 gap-4'>
        <LogoUploader label='Logo (light)' value={brand.logo_url} onChange={(url) => set('logo_url', url)} testid='brand-logo' />
        <LogoUploader label='Logo (dark bg)' value={brand.logo_dark_url} onChange={(url) => set('logo_dark_url', url)} testid='brand-logo-dark' />
        <LogoUploader label='Favicon' value={brand.favicon_url} onChange={(url) => set('favicon_url', url)} testid='brand-favicon' />
      </div>

      <div className='grid md:grid-cols-2 gap-4'>
        <FormRow label='Primary color' hint='Hex format like #0d3b7a'>
          <div className='flex gap-2 items-center'>
            <Input value={brand.primary_color || ''} onChange={e => set('primary_color', e.target.value)} placeholder='#0d3b7a' data-testid='brand-primary-color' />
            <div className='h-9 w-9 rounded border shrink-0' style={{ background: brand.primary_color || '#0d3b7a' }} />
          </div>
        </FormRow>
        <FormRow label='Secondary color' hint='Muted backgrounds & subtle accents'>
          <div className='flex gap-2 items-center'>
            <Input value={brand.secondary_color || ''} onChange={e => set('secondary_color', e.target.value)} placeholder='#f5f7fa' data-testid='brand-secondary-color' />
            <div className='h-9 w-9 rounded border shrink-0' style={{ background: brand.secondary_color || '#f5f7fa' }} />
          </div>
        </FormRow>
      </div>

      <FormRow label='Meta description (SEO)' hint='Shown in search engine snippets. Aim for 150–160 characters.'>
        <Textarea rows={3} value={brand.meta_description || ''} onChange={e => set('meta_description', e.target.value)} maxLength={300} data-testid='brand-meta-desc' />
        <div className='text-[11px] text-muted-foreground text-right mt-1'>{(brand.meta_description || '').length}/300</div>
      </FormRow>

      <FormRow label='Open Graph image URL' hint='Used when links are shared on social media. Recommended 1200×630.'>
        <Input value={brand.og_image_url || ''} onChange={e => set('og_image_url', e.target.value)} placeholder='https://…' data-testid='brand-og-image' />
      </FormRow>

      <div className='grid md:grid-cols-3 gap-4'>
        <FormRow label='Support email'>
          <Input type='email' value={brand.support_email || ''} onChange={e => set('support_email', e.target.value)} data-testid='brand-support-email' />
        </FormRow>
        <FormRow label='Support phone'>
          <Input value={brand.support_phone || ''} onChange={e => set('support_phone', e.target.value)} placeholder='+91 …' data-testid='brand-support-phone' />
        </FormRow>
        <FormRow label='Twitter handle'>
          <Input value={brand.twitter_handle || ''} onChange={e => set('twitter_handle', e.target.value)} placeholder='@indusfact' data-testid='brand-twitter-handle' />
        </FormRow>
      </div>

      <FormRow label='Registered address' hint='Shown in footer & invoices'>
        <Textarea rows={2} value={brand.address || ''} onChange={e => set('address', e.target.value)} data-testid='brand-address' />
      </FormRow>

      <div>
        <div className='text-sm font-semibold mb-2'>Social profiles</div>
        <div className='grid md:grid-cols-2 gap-3'>
          {['linkedin', 'twitter', 'facebook', 'instagram', 'youtube'].map(k => (
            <FormRow key={k} label={k.charAt(0).toUpperCase() + k.slice(1)}>
              <Input value={(brand.social || {})[k] || ''} onChange={e => setSocial(k, e.target.value)} placeholder={`https://${k}.com/…`} data-testid={`brand-social-${k}`} />
            </FormRow>
          ))}
        </div>
      </div>

      <div className='flex items-center gap-2 justify-end pt-4 border-t'>
        <Button variant='outline' onClick={onReset} disabled={saving} data-testid='brand-reset-btn'>
          <RotateCcw className='h-4 w-4 mr-1' /> Reset to defaults
        </Button>
        <Button onClick={onSave} disabled={saving} data-testid='brand-save-btn'>
          <Save className='h-4 w-4 mr-1' /> {saving ? 'Saving…' : 'Save brand settings'}
        </Button>
      </div>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Content rules form
// ---------------------------------------------------------------------------
const RULE_ROWS = [
  { section: 'Listings', items: [
    { key: 'listing_min_title_len', label: 'Title — min characters', hint: 'Prevents 1-word listings' },
    { key: 'listing_max_title_len', label: 'Title — max characters' },
    { key: 'listing_min_description_len', label: 'Description — min characters', hint: 'Enforces detailed listings' },
    { key: 'listing_max_description_len', label: 'Description — max characters' },
    { key: 'listing_min_images', label: 'Minimum images (Sell only)' },
    { key: 'listing_max_images', label: 'Maximum media items' },
    { key: 'listing_min_price', label: 'Minimum price (₹)' },
    { key: 'listing_max_price', label: 'Maximum price (₹)' },
  ]},
  { section: 'Reviews', items: [
    { key: 'review_min_body_len', label: 'Review body — min characters' },
    { key: 'review_max_body_len', label: 'Review body — max characters' },
  ]},
  { section: 'Inquiries', items: [
    { key: 'inquiry_min_message_len', label: 'Inquiry message — min characters' },
    { key: 'inquiry_max_message_len', label: 'Inquiry message — max characters' },
  ]},
];

const RulesForm = ({ rules, setRules, saving, onSave, onReset }) => {
  const set = (k, raw) => {
    const v = raw === '' ? null : Number(raw);
    setRules({ ...rules, [k]: v });
  };
  return (
    <Card className='p-5 space-y-6' data-testid='content-rules-form'>
      <div>
        <div className='text-lg font-semibold'>Content rules</div>
        <p className='text-sm text-muted-foreground mt-0.5'>
          Hard limits enforced when users create listings, reviews, or inquiries.
          Set a min/max pair to 0 to disable it. Staff bypass moderation rules but
          <strong> not </strong> these length limits.
        </p>
      </div>

      {RULE_ROWS.map(section => (
        <div key={section.section} className='space-y-3'>
          <div className='text-sm font-semibold flex items-center gap-2'>
            <Badge variant='outline' className='text-[10px]'>{section.section}</Badge>
          </div>
          <div className='grid md:grid-cols-2 gap-3'>
            {section.items.map(item => (
              <FormRow key={item.key} label={item.label} hint={item.hint}>
                <Input
                  type='number'
                  min='0'
                  value={rules[item.key] ?? ''}
                  onChange={e => set(item.key, e.target.value)}
                  data-testid={`rule-${item.key}`}
                />
              </FormRow>
            ))}
          </div>
        </div>
      ))}

      <div className='flex items-center gap-2 justify-end pt-4 border-t'>
        <Button variant='outline' onClick={onReset} disabled={saving} data-testid='rules-reset-btn'>
          <RotateCcw className='h-4 w-4 mr-1' /> Reset to defaults
        </Button>
        <Button onClick={onSave} disabled={saving} data-testid='rules-save-btn'>
          <Save className='h-4 w-4 mr-1' /> {saving ? 'Saving…' : 'Save content rules'}
        </Button>
      </div>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------
const FormRow = ({ label, hint, children }) => (
  <div className='space-y-1'>
    <Label className='text-xs font-medium text-muted-foreground'>{label}</Label>
    {children}
    {hint && <p className='text-[11px] text-muted-foreground'>{hint}</p>}
  </div>
);

/**
 * Reuses the existing /api/media/upload/image endpoint to store logos /
 * favicon so we don't need a new endpoint just for brand assets.
 */
const LogoUploader = ({ label, value, onChange, testid }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const backendBase = process.env.REACT_APP_BACKEND_URL || '';

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file (PNG / JPG / WebP / SVG).');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image is too large. Max 2 MB.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/media/upload/image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Admin brand assets (logo, favicon, OG image) must never carry the
        // IndusFact watermark — the watermark is only for listing photos.
        params: { context: 'admin_upload' },
      });
      // Media endpoint returns { public_url, thumb_url, ... } — normalize
      // to `.url` where legacy code expects it.
      onChange(data.public_url || data.url);
      toast.success(`${label} uploaded`);
    } catch (e) {
      toast.error(extractApiError(e, 'Upload failed'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const previewSrc = value ? (value.startsWith('http') ? value : `${backendBase}${value}`) : '';

  return (
    <div className='space-y-1'>
      <Label className='text-xs font-medium text-muted-foreground'>{label}</Label>
      <div className='flex items-center gap-2'>
        <div className='h-12 w-12 rounded border bg-muted flex items-center justify-center overflow-hidden shrink-0'>
          {previewSrc ? (
            <img src={previewSrc} alt={label} className='max-h-full max-w-full object-contain' />
          ) : (
            <ImageIcon className='h-5 w-5 text-muted-foreground' />
          )}
        </div>
        <input
          ref={fileRef}
          type='file'
          accept='image/*'
          className='hidden'
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid={`${testid}-file`}
        />
        <Button
          type='button'
          size='sm'
          variant='outline'
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`${testid}-upload-btn`}
        >
          {uploading ? <Loader2 className='h-3.5 w-3.5 animate-spin mr-1' /> : <Upload className='h-3.5 w-3.5 mr-1' />}
          {value ? 'Replace' : 'Upload'}
        </Button>
        {value && (
          <Button
            type='button'
            size='sm'
            variant='ghost'
            className='text-destructive hover:text-destructive'
            onClick={() => onChange('')}
            data-testid={`${testid}-remove-btn`}
          >
            Remove
          </Button>
        )}
      </div>
    </div>
  );
};

export default AdminSettingsTab;

// ---------------------------------------------------------------------------
// Razorpay / Payments config card
// ---------------------------------------------------------------------------
const RazorpayConfigCard = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [keyId, setKeyId] = useState('');
  const [keySecret, setKeySecret] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [showWebhook, setShowWebhook] = useState(false);
  // Baseline for comparing changed fields. Guarded so a StrictMode-driven
  // double-mount / second load() does NOT overwrite user edits.
  const baselineKeyIdRef = useRef('');
  const initializedRef = useRef(false);
  const loadInFlightRef = useRef(false);

  const load = async ({ force = false } = {}) => {
    if (loadInFlightRef.current) return;
    loadInFlightRef.current = true;
    setLoading(true);
    try {
      const { data } = await api.get('/admin/payments/config');
      setCfg(data);
      // Only seed the form inputs ONCE (first successful load) or when the
      // caller explicitly forces a resync (e.g. after Save).
      if (!initializedRef.current || force) {
        const loadedId = data.razorpay_key_id || '';
        setKeyId(loadedId);
        setKeySecret('');
        setWebhookSecret('');
        baselineKeyIdRef.current = loadedId;
        initializedRef.current = true;
      }
    } catch (e) {
      toast.error(extractApiError(e, 'Failed to load payment config'));
    } finally {
      setLoading(false);
      loadInFlightRef.current = false;
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      // Include key_id only if the admin actually changed it
      if (keyId !== baselineKeyIdRef.current) payload.razorpay_key_id = keyId;
      // Only send secrets if user typed something (empty = leave unchanged)
      if (keySecret !== '') payload.razorpay_key_secret = keySecret;
      if (webhookSecret !== '') payload.razorpay_webhook_secret = webhookSecret;
      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save.');
        return;
      }
      const { data } = await api.put('/admin/payments/config', payload);
      setCfg(data);
      // Reset baseline + clear secret inputs (masked value stays visible)
      baselineKeyIdRef.current = data.razorpay_key_id || '';
      setKeySecret('');
      setWebhookSecret('');
      toast.success('Razorpay config saved. Changes take effect immediately.');
    } catch (e) {
      toast.error(extractApiError(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const payload = {};
      // If admin typed new keys, test THOSE (unsaved) — otherwise test saved
      if (keyId && keySecret) {
        payload.razorpay_key_id = keyId;
        payload.razorpay_key_secret = keySecret;
      }
      const { data } = await api.post('/admin/payments/test', payload);
      toast.success(`Connected ✓ mode=${data.mode} · order=${data.order_id}`);
    } catch (e) {
      toast.error(extractApiError(e, 'Test failed — check keys'));
    } finally { setTesting(false); }
  };

  if (loading || !cfg) {
    return <Card className='p-6 text-sm text-muted-foreground' data-testid='razorpay-loading'>Loading Razorpay config…</Card>;
  }

  const sourceBadge = (s) => {
    if (s === 'db') return <Badge className='bg-emerald-100 text-emerald-800 border-emerald-200'>Saved in DB</Badge>;
    if (s === 'env') return <Badge className='bg-blue-100 text-blue-800 border-blue-200'>From .env fallback</Badge>;
    return <Badge variant='outline' className='text-muted-foreground'>Not set</Badge>;
  };

  return (
    <Card className='p-5 space-y-6' data-testid='razorpay-config-card'>
      <div className='flex items-start justify-between gap-4'>
        <div>
          <div className='text-lg font-semibold flex items-center gap-2'>
            <CreditCard className='h-5 w-5' /> Razorpay / Payments
          </div>
          <p className='text-sm text-muted-foreground mt-0.5'>
            Add your Razorpay keys here. Changes take effect on the very next payment call — no server restart needed.
          </p>
        </div>
        <div className='flex flex-col items-end gap-1.5'>
          {cfg.razorpay_enabled ? (
            <Badge className='bg-emerald-600 text-white'><ShieldCheck className='h-3 w-3 mr-1' /> Razorpay LIVE</Badge>
          ) : (
            <Badge variant='outline' className='text-amber-700 border-amber-300 bg-amber-50'>Disabled — using manual fallback</Badge>
          )}
          {cfg.updated_at && (
            <div className='text-xs text-muted-foreground'>
              Updated by {cfg.updated_by_email || '—'} · {new Date(cfg.updated_at).toLocaleString()}
            </div>
          )}
        </div>
      </div>

      <div className='space-y-4'>
        <div className='space-y-1.5'>
          <div className='flex items-center justify-between'>
            <Label htmlFor='rzp-key-id' className='text-sm'>Razorpay Key ID</Label>
            {sourceBadge(cfg.source?.key_id)}
          </div>
          <Input
            id='rzp-key-id'
            value={keyId}
            onChange={e => setKeyId(e.target.value)}
            placeholder='rzp_test_xxxxxxxxxxxxxx or rzp_live_xxxxxxxxxxxxxx'
            data-testid='rzp-key-id-input'
          />
          <p className='text-xs text-muted-foreground'>Public identifier. Safe to display. `rzp_test_*` = test mode, `rzp_live_*` = production.</p>
        </div>

        <div className='space-y-1.5'>
          <div className='flex items-center justify-between'>
            <Label htmlFor='rzp-key-secret' className='text-sm'>Razorpay Key Secret</Label>
            <div className='flex items-center gap-2'>
              {cfg.razorpay_key_secret_masked && (
                <span className='text-xs text-muted-foreground font-mono'>{cfg.razorpay_key_secret_masked}</span>
              )}
              {sourceBadge(cfg.source?.key_secret)}
            </div>
          </div>
          <div className='flex items-center gap-2'>
            <Input
              id='rzp-key-secret'
              type={showSecret ? 'text' : 'password'}
              value={keySecret}
              onChange={e => setKeySecret(e.target.value)}
              placeholder={cfg.razorpay_key_secret_masked ? 'Leave blank to keep existing secret' : 'Paste your Razorpay Key Secret'}
              data-testid='rzp-key-secret-input'
              autoComplete='new-password'
            />
            <Button type='button' variant='outline' size='sm' onClick={() => setShowSecret(v => !v)} data-testid='rzp-key-secret-toggle'>
              {showSecret ? 'Hide' : 'Show'}
            </Button>
          </div>
        </div>

        <div className='space-y-1.5'>
          <div className='flex items-center justify-between'>
            <Label htmlFor='rzp-webhook-secret' className='text-sm'>Razorpay Webhook Secret</Label>
            <div className='flex items-center gap-2'>
              {cfg.razorpay_webhook_secret_masked && (
                <span className='text-xs text-muted-foreground font-mono'>{cfg.razorpay_webhook_secret_masked}</span>
              )}
              {sourceBadge(cfg.source?.webhook_secret)}
            </div>
          </div>
          <div className='flex items-center gap-2'>
            <Input
              id='rzp-webhook-secret'
              type={showWebhook ? 'text' : 'password'}
              value={webhookSecret}
              onChange={e => setWebhookSecret(e.target.value)}
              placeholder={cfg.razorpay_webhook_secret_masked ? 'Leave blank to keep existing secret' : 'Paste your Razorpay Webhook Secret'}
              data-testid='rzp-webhook-secret-input'
              autoComplete='new-password'
            />
            <Button type='button' variant='outline' size='sm' onClick={() => setShowWebhook(v => !v)} data-testid='rzp-webhook-secret-toggle'>
              {showWebhook ? 'Hide' : 'Show'}
            </Button>
          </div>
          <p className='text-xs text-muted-foreground'>
            Set your webhook URL in Razorpay dashboard to&nbsp;
            <code className='font-mono bg-muted px-1 rounded'>{`${window.location.origin.replace(/\/$/, '')}/api/payments/webhook/razorpay`}</code>
            &nbsp;and paste the signing secret here.
          </p>
        </div>
      </div>

      <div className='flex flex-wrap items-center gap-2 pt-2 border-t'>
        <Button onClick={save} disabled={saving} data-testid='rzp-save-btn'>
          {saving ? <Loader2 className='h-4 w-4 mr-1 animate-spin' /> : <Save className='h-4 w-4 mr-1' />}
          Save
        </Button>
        <Button variant='outline' onClick={test} disabled={testing} data-testid='rzp-test-btn'>
          {testing ? <Loader2 className='h-4 w-4 mr-1 animate-spin' /> : <KeyRound className='h-4 w-4 mr-1' />}
          Test connection
        </Button>
        <span className='text-xs text-muted-foreground ml-auto'>
          Empty secret fields on save = leave the stored value unchanged.
        </span>
      </div>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Footer form
// ---------------------------------------------------------------------------
const FooterForm = ({ footer, setFooter, saving, onSave, onReset }) => {
  const cols = footer.columns || [];

  const setField = (k, v) => setFooter({ ...footer, [k]: v });

  const setCol = (idx, patch) => {
    const next = cols.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    setFooter({ ...footer, columns: next });
  };
  const addCol = () => {
    if (cols.length >= 6) { toast.warning('Max 6 columns'); return; }
    const id = `col_${Date.now().toString(36)}`;
    setFooter({ ...footer, columns: [...cols, { id, heading: 'New column', links: [] }] });
  };
  const removeCol = (idx) => {
    if (!window.confirm(`Remove column "${cols[idx].heading}" and all its links?`)) return;
    setFooter({ ...footer, columns: cols.filter((_, i) => i !== idx) });
  };
  const moveCol = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= cols.length) return;
    const next = [...cols];
    [next[idx], next[j]] = [next[j], next[idx]];
    setFooter({ ...footer, columns: next });
  };

  const setLink = (colIdx, linkIdx, patch) => {
    const next = cols.map((c, i) => {
      if (i !== colIdx) return c;
      const links = (c.links || []).map((ln, j) => (j === linkIdx ? { ...ln, ...patch } : ln));
      return { ...c, links };
    });
    setFooter({ ...footer, columns: next });
  };
  const addLink = (colIdx) => {
    const next = cols.map((c, i) => {
      if (i !== colIdx) return c;
      const links = [...(c.links || [])];
      if (links.length >= 20) { toast.warning('Max 20 links per column'); return c; }
      links.push({ label: 'New link', url: '/' });
      return { ...c, links };
    });
    setFooter({ ...footer, columns: next });
  };
  const removeLink = (colIdx, linkIdx) => {
    const next = cols.map((c, i) => {
      if (i !== colIdx) return c;
      return { ...c, links: (c.links || []).filter((_, j) => j !== linkIdx) };
    });
    setFooter({ ...footer, columns: next });
  };
  const moveLink = (colIdx, linkIdx, dir) => {
    const links = [...(cols[colIdx].links || [])];
    const j = linkIdx + dir;
    if (j < 0 || j >= links.length) return;
    [links[linkIdx], links[j]] = [links[j], links[linkIdx]];
    setCol(colIdx, { links });
  };

  return (
    <Card className='p-5 space-y-6' data-testid='footer-settings-form'>
      <div>
        <div className='text-lg font-semibold'>Footer content</div>
        <p className='text-sm text-muted-foreground mt-0.5'>
          Manage the footer that appears at the bottom of every page. Reorder columns
          and links with the up/down arrows.
        </p>
      </div>

      <div className='grid md:grid-cols-2 gap-4'>
        <FormRow label='About text (brand column)' hint='Shown under your logo on the far left of the footer.'>
          <Textarea
            rows={3}
            maxLength={1000}
            value={footer.about_text || ''}
            onChange={e => setField('about_text', e.target.value)}
            data-testid='footer-about-text-input'
          />
          <div className='text-[11px] text-muted-foreground text-right mt-0.5'>{(footer.about_text || '').length}/1000</div>
        </FormRow>
        <FormRow label='Bottom-right tagline' hint='Short line shown on the right of the copyright strip.'>
          <Input
            value={footer.bottom_tagline || ''}
            onChange={e => setField('bottom_tagline', e.target.value)}
            maxLength={200}
            placeholder='Made for Indian industry · Verified · Moderated'
            data-testid='footer-tagline-input'
          />
        </FormRow>
        <FormRow label='Copyright line' hint='Use {year} to auto-insert the current year.'>
          <Input
            value={footer.copyright || ''}
            onChange={e => setField('copyright', e.target.value)}
            maxLength={300}
            placeholder='© {year} IndusFact B2B Solutions. All rights reserved.'
            data-testid='footer-copyright-input'
          />
        </FormRow>
        <div className='flex items-center justify-between rounded-md border p-3'>
          <div>
            <div className='text-sm font-medium'>Show social icons</div>
            <div className='text-xs text-muted-foreground'>Uses the social URLs configured in the Brand tab.</div>
          </div>
          <Switch
            checked={footer.show_social !== false}
            onCheckedChange={v => setField('show_social', v)}
            data-testid='footer-show-social-toggle'
          />
        </div>
      </div>

      <div className='space-y-4'>
        <div className='flex items-center justify-between'>
          <div>
            <div className='text-sm font-semibold'>Link columns</div>
            <div className='text-xs text-muted-foreground'>Up to 6 columns, 20 links each. Empty columns are hidden on the site.</div>
          </div>
          <Button variant='outline' size='sm' onClick={addCol} disabled={cols.length >= 6} data-testid='footer-add-column-btn'>
            <Plus className='h-3.5 w-3.5 mr-1' /> Add column
          </Button>
        </div>

        {cols.length === 0 && (
          <div className='rounded border border-dashed p-6 text-sm text-muted-foreground text-center'>
            No columns yet. Click <strong>Add column</strong> to build your footer navigation.
          </div>
        )}

        {cols.map((col, cIdx) => (
          <Card key={col.id || cIdx} className='p-4 border-2 border-dashed' data-testid={`footer-column-edit-${cIdx}`}>
            <div className='flex items-center gap-2 mb-3'>
              <Badge variant='outline' className='text-[10px]'>Column {cIdx + 1}</Badge>
              <Input
                className='flex-1'
                value={col.heading || ''}
                onChange={e => setCol(cIdx, { heading: e.target.value })}
                placeholder='Column heading'
                maxLength={60}
                data-testid={`footer-col-heading-${cIdx}`}
              />
              <Button size='sm' variant='outline' onClick={() => moveCol(cIdx, -1)} disabled={cIdx === 0} data-testid={`footer-col-up-${cIdx}`}><ArrowUp className='h-3.5 w-3.5' /></Button>
              <Button size='sm' variant='outline' onClick={() => moveCol(cIdx, 1)} disabled={cIdx === cols.length - 1} data-testid={`footer-col-down-${cIdx}`}><ArrowDown className='h-3.5 w-3.5' /></Button>
              <Button size='sm' variant='outline' className='text-destructive hover:text-destructive' onClick={() => removeCol(cIdx)} data-testid={`footer-col-remove-${cIdx}`}>
                <Trash2 className='h-3.5 w-3.5' />
              </Button>
            </div>

            <div className='space-y-2'>
              {(col.links || []).map((ln, lIdx) => (
                <div key={lIdx} className='grid grid-cols-[1fr_1.5fr_auto_auto_auto] gap-2 items-center' data-testid={`footer-link-row-${cIdx}-${lIdx}`}>
                  <Input
                    value={ln.label || ''}
                    onChange={e => setLink(cIdx, lIdx, { label: e.target.value })}
                    placeholder='Label'
                    maxLength={80}
                    data-testid={`footer-link-label-${cIdx}-${lIdx}`}
                  />
                  <Input
                    value={ln.url || ''}
                    onChange={e => setLink(cIdx, lIdx, { url: e.target.value })}
                    placeholder='/path or https://…'
                    maxLength={500}
                    data-testid={`footer-link-url-${cIdx}-${lIdx}`}
                  />
                  <Button size='sm' variant='outline' onClick={() => moveLink(cIdx, lIdx, -1)} disabled={lIdx === 0} data-testid={`footer-link-up-${cIdx}-${lIdx}`}><ArrowUp className='h-3.5 w-3.5' /></Button>
                  <Button size='sm' variant='outline' onClick={() => moveLink(cIdx, lIdx, 1)} disabled={lIdx === (col.links || []).length - 1} data-testid={`footer-link-down-${cIdx}-${lIdx}`}><ArrowDown className='h-3.5 w-3.5' /></Button>
                  <Button size='sm' variant='outline' className='text-destructive hover:text-destructive' onClick={() => removeLink(cIdx, lIdx)} data-testid={`footer-link-remove-${cIdx}-${lIdx}`}>
                    <Trash2 className='h-3.5 w-3.5' />
                  </Button>
                </div>
              ))}
              <Button variant='outline' size='sm' onClick={() => addLink(cIdx)} disabled={(col.links || []).length >= 20} data-testid={`footer-col-add-link-${cIdx}`}>
                <Plus className='h-3.5 w-3.5 mr-1' /> Add link
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <div className='flex items-center gap-2 justify-end pt-4 border-t'>
        <Button variant='outline' onClick={onReset} disabled={saving} data-testid='footer-reset-btn'>
          <RotateCcw className='h-4 w-4 mr-1' /> Reset to defaults
        </Button>
        <Button onClick={onSave} disabled={saving} data-testid='footer-save-btn'>
          <Save className='h-4 w-4 mr-1' /> {saving ? 'Saving…' : 'Save footer'}
        </Button>
      </div>
    </Card>
  );
};
